import { v4 } from 'uuid';

import { Message as MessageType } from '~/features/ask-anything/types/chat';
import { getWorkflowPath } from '~/utils/workflow.utils';

import type { AgentMessage, MessageContext } from '../types';
import type {
  WorkflowRecommendation,
  WorkflowSuggestion,
} from './WorkflowRoutingAgent';

export const AGENT_TYPE = 'wf_router' as const;
export const EXECUTION_TAG = 'workflow_routing' as const;

export const AI_USER = {
  id: 'evisort-ai-workflow-router',
  firstName: 'Evisort',
  lastName: 'AI',
  email: 'ai@evisort.com',
} as const;

export interface PendingUserMessage {
  id: string;
  text: string;
  timestamp: Date;
  messageType?: 'query' | 'command';
}

export const createWorkflowRoutingExecutionConfig = () => ({
  externalEntities: [],
  metadata: {},
});

export const transformAgentMessage = (msg: AgentMessage): MessageType => {
  let messageType: 'query' | 'command' | undefined = undefined;

  if (msg.userType === 'current') {
    if (
      msg.messageContext?.messageType === 'query' ||
      msg.messageContext?.messageType === 'command'
    ) {
      messageType = msg.messageContext.messageType;
    } else {
      messageType = 'query';
    }
  }

  return {
    id: msg.id,
    text: msg.text,
    userType: msg.userType,
    timestamp: msg.timestamp,
    choices: msg.responseSuggestions ?? [],
    citations: msg.citations ?? [],
    sources: msg.sources ?? [],
    messageType,
  };
};

export const transformPendingMessage = (
  pending: PendingUserMessage,
): MessageType => {
  return {
    id: pending.id,
    text: pending.text,
    userType: 'current' as const,
    timestamp: pending.timestamp,
    choices: [],
    citations: [],
    sources: [],
    messageType: pending.messageType,
  };
};

export const shouldDisplayMessage = (
  msg: AgentMessage,
  allMessages: AgentMessage[],
  pendingMessages: PendingUserMessage[],
): boolean => {
  if (msg.messageContext?.messageType === 'action') {
    return false;
  }

  const hasText = msg.text.trim() !== '';
  if (!hasText) {
    return false;
  }

  if (
    msg.userType === 'other' &&
    msg.responseSuggestions &&
    msg.responseSuggestions.length > 0
  ) {
    const isInitialSuggestionsMessage =
      allMessages.length > 0 && allMessages[0].id === msg.id;

    const hasUserMessages = allMessages.some((m) => m.userType === 'current');
    const hasPendingUserMessages = pendingMessages.length > 0;

    if (
      isInitialSuggestionsMessage &&
      (hasUserMessages || hasPendingUserMessages)
    ) {
      return false;
    }

    const hasSelectedChoice = msg.responseSuggestions.some((choice) => {
      const normalizedChoice = choice.trim().toLowerCase();

      const foundInMessages = allMessages.some(
        (laterMsg) =>
          laterMsg.userType === 'current' &&
          laterMsg.text.trim().toLowerCase() === normalizedChoice,
      );

      const foundInPending = pendingMessages.some(
        (pendingMsg) =>
          pendingMsg.text.trim().toLowerCase() === normalizedChoice,
      );

      return foundInMessages || foundInPending;
    });

    if (hasSelectedChoice) {
      return false;
    }
  }

  return true;
};

export const createMessageContext = (
  messageType: 'query' | 'command',
): MessageContext => ({
  messageType,
});

export const convertWorkflowRecommendationToSuggestion = (
  recommendation:
    | WorkflowRecommendation
    | {
        workflow_id?: string;
        workflowId?: string;
        published_workflow_id?: string | null;
        publishedWorkflowId?: string | null;
        workflow_name?: string;
        workflowName?: string;
        reasoning: string;
        confidence: 'high' | 'medium' | 'low';
      },
): WorkflowSuggestion => {
  const workflowId =
    (recommendation as any).workflow_id ?? (recommendation as any).workflowId;
  const publishedWorkflowId =
    (recommendation as any).published_workflow_id ??
    (recommendation as any).publishedWorkflowId;
  const workflowName =
    (recommendation as any).workflow_name ??
    (recommendation as any).workflowName;

  return {
    workflowId: workflowId!,
    publishedWorkflowId: publishedWorkflowId ?? null,
    workflowName: workflowName!,
    reasoning: recommendation.reasoning,
    confidence: recommendation.confidence,
    permanentLinkId: publishedWorkflowId ?? workflowId!,
    wssDesignated: false,
  };
};

export const extractWorkflowRecommendations = (
  message: AgentMessage,
): WorkflowSuggestion[] => {
  const additionalInfo = message.additionalInfo as
    | {
        workflow_recommendations?: WorkflowRecommendation[];
        workflowRecommendations?: WorkflowRecommendation[];
      }
    | undefined;

  const recommendations =
    additionalInfo?.workflow_recommendations ??
    additionalInfo?.workflowRecommendations;

  if (!recommendations || recommendations.length === 0) {
    return [];
  }

  return recommendations.map(convertWorkflowRecommendationToSuggestion);
};

export const createPendingMessage = (
  text: string,
  messageType?: 'query' | 'command',
): PendingUserMessage => ({
  id: v4(),
  text,
  timestamp: new Date(),
  messageType,
});

export const buildWorkflowUrl = (
  workflow: { permanentLinkId: string; wssDesignated: boolean },
  subDomain: string,
): string => {
  const workflowItem = {
    permanentLinkId: workflow.permanentLinkId,
    wssDesignated: workflow.wssDesignated,
  };
  const path = getWorkflowPath(workflowItem);
  return `${window.location.origin}/${subDomain}${path}`;
};
